import * as React from 'react';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';

type ChatBubbleProps = MessageProps & {
    variant: 'sent' | 'received';
    customerFirstName?: string;
};

function formatShortTimestamp(raw: string): string {
    try {
        const d = new Date(raw);
        return d.toLocaleString(undefined, {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
    } catch {
        return raw;
    }
}

export default function ChatBubble(props: ChatBubbleProps) {
    const {content, variant, timestamp, attachment = undefined, sender, channel, customerFirstName} = props;
    const isSent = variant === 'sent';
    const isAI = sender.is_ai || sender.username?.endsWith('.ai') || false;
    const channelLabel = (channel || 'SMS').toUpperCase();
    const timeLabel = timestamp ? formatShortTimestamp(timestamp) : '';

    const senderName = isAI
        ? null // handled separately
        : isSent
            ? (sender.name || sender.username || 'You')
            : (customerFirstName || sender.name || 'Customer');

    // ---------- Ronald AI messages ----------
    if (isAI && !attachment) {
        return (
            <Box sx={{maxWidth: '70%', minWidth: 100}}>
                <Box
                    sx={{
                        borderRadius: '8px',
                        p: 1.5,
                        background: 'linear-gradient(to bottom right, #4f46e5, #7c3aed, #6d28d9)',
                        color: '#fff',
                        border: '1px solid rgba(167,139,250,0.3)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    {/* Sender label — inside the bubble so light colors are visible */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75}}>
                        <SmartToyRoundedIcon sx={{fontSize: 12, color: '#e9d5ff'}} aria-hidden/>
                        <Typography
                            level="body-xs"
                            sx={{
                                fontWeight: 800,
                                background: 'linear-gradient(90deg, #e9d5ff, #fbcfe8)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: '10px',
                            }}
                        >
                            Ronald
                        </Typography>
                        <Box
                            component="span"
                            sx={{
                                borderRadius: '9999px',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                px: 0.75,
                                py: '1px',
                                fontSize: '8px',
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: '#e9d5ff',
                            }}
                        >
                            ai
                        </Box>
                    </Box>
                    <Typography
                        level="body-sm"
                        sx={{color: '#fff', whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}
                    >
                        {content}
                    </Typography>
                    <Typography level="body-xs" sx={{mt: 0.5, textAlign: 'right', color: 'rgba(199,186,255,0.7)', fontSize: '10px'}}>
                        {channelLabel} • {timeLabel}
                    </Typography>
                </Box>
            </Box>
        );
    }

    // ---------- Attachment ----------
    if (attachment) {
        return (
            <Box sx={{maxWidth: '70%', minWidth: 100}}>
                <Typography level="body-xs" sx={{mb: 0.25, fontSize: '10px', fontWeight: 600, color: isSent ? '#93c5fd' : '#9ca3af'}}>
                    {senderName}
                </Typography>
                <Sheet
                    variant="outlined"
                    sx={{
                        px: 1.75,
                        py: 1.25,
                        borderRadius: '8px',
                    }}
                >
                    <Stack direction="row" spacing={1.5} sx={{alignItems: 'center'}}>
                        <Avatar color="primary" size="lg">
                            <InsertDriveFileRoundedIcon/>
                        </Avatar>
                        <div>
                            <Typography sx={{fontSize: 'sm'}}>{attachment.fileName}</Typography>
                            <Typography level="body-sm">{attachment.size}</Typography>
                        </div>
                    </Stack>
                </Sheet>
            </Box>
        );
    }

    // ---------- Normal messages ----------
    return (
        <Box sx={{maxWidth: '70%', minWidth: 100}}>
            {/* Sender label */}
            <Typography level="body-xs" sx={{mb: 0.25, fontSize: '10px', fontWeight: 600, color: isSent ? '#93c5fd' : '#9ca3af'}}>
                {senderName}
            </Typography>
            <Box
                sx={{
                    borderRadius: '8px',
                    p: 1.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    ...(isSent
                        ? {bgcolor: '#2563eb', color: '#fff'}
                        : {bgcolor: '#fff', border: '1px solid', borderColor: '#e5e7eb', color: '#1f2937'}),
                }}
            >
                <Typography
                    level="body-sm"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        color: isSent ? '#fff' : '#1f2937',
                    }}
                >
                    {content}
                </Typography>
                <Typography
                    level="body-xs"
                    sx={{
                        mt: 0.5,
                        textAlign: 'right',
                        fontSize: '10px',
                        color: isSent ? '#93c5fd' : '#9ca3af',
                    }}
                >
                    {channelLabel} • {timeLabel}
                </Typography>
            </Box>
        </Box>
    );
}
